import { useEffect, useState } from 'react';
import superagent from 'superagent';
import { useIdToken } from './useIdToken.js';
import config from '../client-config.js';
import qs from 'query-string';
import { PLATFORM } from 'constants/socialMedia.js';
import { ONE_TO_MANY_PRODUCT } from 'constants/products.js';
import { openPopup, waitForRedirectCallback, PopupBlockedError, PopupClosedError } from '../helpers/popup.jsx';
import { HTTP_ERROR_CODE } from 'constants/http.js';

export class AuthTokenExpiredError extends Error {}
export class AuthWindowBlockedError extends Error {}
export class AuthCancelledError extends Error {}
export class YouTubeAccountNotVerifiedError extends Error {}
export class FacebookMissingPermissionsError extends Error {}
export class FacebookInsufficientRoleError extends Error {}

/** A cache to be used internally in this service. The code is quite simple, thus we dont import a dependency for this. */
const cache = {
  _store: new Map(),
  _ttl: new Map(),
  has(key) {
    return this._store.has(key);
  },
  get(key) {
    return this._store.get(key);
  },
  set(key, value, { ttl = 60 } = {}) {
    this.unset(key);
    this._store.set(key, value);
    this._ttl.set(key, setTimeout(() => this.unset(key), ttl * 1000));
  },
  unset(key) {
    if (!this.has(key)) return true;
    clearTimeout(this._ttl.get(key));
    this._store.delete(key);
    this._ttl.delete(key);
  },
  clear() {
    this._ttl.forEach(clearTimeout);
    this._ttl.clear();
    this._store.clear();
  },
};

/**
 * @typedef CustomRtmpSettings
 * @type {{
 *   customDisplayName: string,
 *   displayName: string,
 *   platform: 'customRtmp'
 *   streamKey: string,
 *   streamUrl: string,
 * }}
 */

/**
 * @typedef FacebookPage
 * @type {{
 *   id: string
 *   displayName: string
 *   pageId: string
 *   pageName: string
 *   pageToken: string
 *   platform: 'facebook'
 * }}
 */

/**
 * @typedef YoutubeChannel
 * @type {{
 *   connectionName: string
 * }}
 */

/**
 * @typedef RequestOptions
 * @property {object} [headers={}]
 * @property {object} [params={}]
 * @property {any} [body]
 */

/**
 * @callback Request
 * @param {string} path
 * @param {any} body
 * @param {RequestOptions} options
 * @returns {superagent.SuperAgentRequest}
 */

/**
 * Creates a socialMediaAgent
 * @param {string} orgId The organization id for the agent
 * @param {string} idToken The id token to use for the agent
 */
const makeSocialMediaAgent = (orgId, idToken) => ({
  instance: {
    /**
     * @template {'get'|'post'|'put'|'patch'|'delete'} T
     * @param {T} method
     * @param {string} path
     * @param {RequestOptions} options
     * @returns {ReturnType<superagent.SuperAgentStatic[T]> extends superagent.SuperAgentRequest ? superagent.SuperAgentRequest : never}
     */
    req(method, path, { headers = {}, body, params = {} } = {}) {
      const region = encodeURIComponent(config.region || 'us-central1');
      const projectId = encodeURIComponent(config.projectId);
      const baseUrl = config.socialMediaBaseUrl || `https://${region}-${projectId}.cloudfunctions.net/socialMedia`;

      return superagent[method](`${baseUrl}/${path}`)
        .set({ 'content-type': 'application/json', ...headers, Authorization: `Bearer ${idToken}` })
        .query(params)
        .send(body);
    },
    /** @type {(path: string, options: RequestOptions) => superagent.SuperAgentRequest} */
    get(path, options) {
      return this.req('get', path, options);
    },
    /** @type {Request} */
    post(path, body, options) {
      return this.req('post', path, { body, ...options });
    },
    /** @type {Request} */
    put(path, body, options) {
      return this.req('put', path, { body, ...options });
    },
    /** @type {Request} */
    patch(path, body, options) {
      return this.req('patch', path, { body, ...options });
    },
    /** @type {(path: string, options: RequestOptions) => superagent.SuperAgentRequest} */
    delete(path, options) {
      return this.req('delete', path, { ...options });
    },
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @returns {Promise<any>}
   */
  async checkToken(platform, connectionId) {
    if (platform === PLATFORM.INSTAGRAM) platform = PLATFORM.FACEBOOK; // reuse the (relatively naïve) facebook endpoint for instagram
    const response = await this.instance.get(`${orgId}/${platform}/checkToken`, { params: { connectionId } });
    return response.body;
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @returns {Promise<{ sessionId: string, authUri: string }>}
   */
  async startConnect({ platform, redirectUri, scopes, configurationId, product }) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    const response = await this.instance.post(
      `${orgId}/oauth/start?${queryParams.toString()}`,
      { platform, redirectUri, scopes, configurationId },
    );
    return response.body;
  },

  /**
   * @param {string} platform
   * @param {string} connectionId
   * @param {string} code
   * @param {string} state
   * @param {string} product
   * @returns {Promise<any>}
   */
  async completeConnect(platform, sessionId, code, state, product) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    const response = await this.instance.post(
      `${orgId}/oauth/complete?${queryParams.toString()}`,
      { platform, sessionId, code, state },
    );
    return response.body;
  },

  /**
   * @returns {Promise<FacebookPage[]>}
   */
  async getConnectedPages(platform, product) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    const response = await this.instance.get(`${orgId}/${platform}/connectedPages?${queryParams.toString()}`);
    return response.body;
  },

  /**
   * @returns {Promise<YoutubeChannel>}
   */
  async getYoutubeChannel() {
    const response = await this.instance.get(`${orgId}/youtube/channel`);
    return response.body;
  },

  /**
   * @param {string} connectionId
   * @returns {Promise<void>}
   */
  async deleteFacebookPageConnection(connectionId, product) {
    const queryParams = new URLSearchParams();
    queryParams.set('product', product);
    await this.instance.delete(`${orgId}/facebook/page/${connectionId}?${queryParams.toString()}`);
  },

  /**
   * @returns {Promise<void>}
   */
  async deleteYoutubeConnection() {
    await this.instance.delete(`${orgId}/youtube`);
  },

  /**
   * @param {string} connectionId
   * @param {FacebookPage} data
   * @returns {Promise<void>}
   */
  async updateFacebookConnectionPage(connectionId, data) {
    await this.instance.put(`${orgId}/facebook/page/${connectionId}`, data);
  },

  /**
   * @param {string} connectionId
   * @returns {Promise<void>}
   */
  async syncFacebookPageDetails(connectionId) {
    await this.instance.post(`${orgId}/facebook/page/${connectionId}/sync/pageDetails`);
  },

  /**
   * @returns {Promise<CustomRtmpSettings[]>}
   */
  async getRTMPConnections() {
    const response = await this.instance.get(`${orgId}/customRtmp/platforms`);
    return response.body;
  },

  /**
   * @param {CustomRtmpSettings} data
   * @returns {Promise<string>}
   */
  async createRTMPConnection(data) {
    const response = await this.instance.post(`${orgId}/customRtmp/platforms`, data);
    return response.body;
  },

  /**
   * @param {string} rtmpId
   * @param {CustomRtmpSettings} data
   * @returns {Promise<void>}
   */
  async updateRTMPConnection(rtmpId, data) {
    await this.instance.put(`${orgId}/customRtmp/platforms/${rtmpId}`, data);
  },

  /**
   * @param {string} rtmpId
   * @returns {Promise<void>}
   */
  async deleteRTMPConnection(rtmpId) {
    await this.instance.delete(`${orgId}/customRtmp/platforms/${rtmpId}`);
  },
});

/**
 * Creates a social media service instance for the organization id using the id token
 * @param {string} orgId
 * @param {string} idToken
 */
const genService = (orgId, idToken) => {
  if (!idToken) return null;

  const socialMediaAgent = makeSocialMediaAgent(orgId, idToken);

  return {
    /**
     * @param {string} platform
     * @param {{ id?: string }} [options]
     * @returns {Promise<boolean>}
     */
    async isSocialMediaTokenValid(platform, { id } = {}) {
      if (platform === PLATFORM.CUSTOM_RTMP) return true;
      const cacheKey = `${orgId}:${idToken}:${platform}:${id}`;
      if (cache.has(cacheKey)) return cache.get(cacheKey);

      const result = socialMediaAgent.checkToken(platform, id).then(() => true).catch(() => false);
      cache.set(cacheKey, result);
      return result;
    },

    /**
     * @param {string} platform
     * @param {{ scopes?: string[] }} [options]
     * @returns {Promise<void>}
     */
    async connect(platform, { scopes, configurationId, product } = {}) {
      // Start by opening the popup, so that the browser recognises it as a direct result of a user action
      const redirectWindow = openPopup({ target: 'SocialMediaAuth' });
      const redirectDomain = config[platform]?.redirectDomain || `${window.top.location.protocol}//${window.top.location.host}`;
      const redirectUri = `${redirectDomain}/redirect-callback`;
      const { sessionId, authUri } = await socialMediaAgent.startConnect({ platform, redirectUri, scopes, configurationId, product });
      // Redirect the popup to the Uri provided by the API.
      redirectWindow.location.href = authUri;

      try {
        const completionHref = await waitForRedirectCallback(redirectWindow, 'SocialMediaAuth');
        const { code, state, error } = qs.parseUrl(completionHref).query;
        if (error) throw new AuthCancelledError();

        await socialMediaAgent.completeConnect(platform, sessionId, code, state, product);

        cache.clear();
      } catch (err) {
        const errorCode = err.response && err.response.body && err.response.body.errorCode;
        if (err instanceof PopupClosedError) throw new AuthCancelledError();
        if (err instanceof PopupBlockedError) throw new AuthWindowBlockedError();
        if (errorCode === HTTP_ERROR_CODE.ACCOUNT_NOT_VERIFIED) throw new YouTubeAccountNotVerifiedError();
        if (errorCode === HTTP_ERROR_CODE.MISSING_PERMISSIONS) throw new FacebookMissingPermissionsError();
        if (errorCode === HTTP_ERROR_CODE.INSUFFICIENT_ROLE) throw new FacebookInsufficientRoleError();
        throw err;
      }
    },

    /**
     * @param {string} platform
     * @param {string} product The product to filter the connected pages by. Defaults to ONE_TO_MANY_PRODUCT
     * @returns {Promise<({ connectionName: string } & FacebookPage)[]>}
     */
    async getConnectedPages(platform, product = ONE_TO_MANY_PRODUCT) {
      try {
        const { connectedPages } = await socialMediaAgent.getConnectedPages(platform, product);
        return (connectedPages || []).map(page => ({
          ...page,
          connectionName: page.customDisplayName || page.pageName,
        })).filter(i => {
          // Legacy entries will not have a products field and will be considered as ONE_TO_MANY_PRODUCT
          return (!i.products && product === ONE_TO_MANY_PRODUCT) || i.products?.includes(product);
        });
      } catch (err) {
        if (err.status && err.status === 401) {
          throw new AuthTokenExpiredError();
        }
        throw err;
      }
    },

    /**
     * @returns {Promise<YoutubeChannel | null>}
     */
    async getYoutubeChannel() {
      try {
        const channel = await socialMediaAgent.getYoutubeChannel();
        const tokenIsValid = await this.isSocialMediaTokenValid(PLATFORM.YOUTUBE);
        return { ...channel, expired: !tokenIsValid };
      } catch (err) {
        if (err.status && err.status === 404) {
          return null;
        }
        throw err;
      }
    },

    /**
     * @param {string} connectionId
     * @pparam {string} product The product associated with the connection to delete. Defaults to ONE_TO_MANY_PRODUCT
     * @returns {Promise<void>}
     */
    async deleteFacebookPageConnection(connectionId, product = ONE_TO_MANY_PRODUCT) {
      await socialMediaAgent.deleteFacebookPageConnection(connectionId, product);
    },

    /**
     * @param {string} connectionId
     * @returns {Promise<void>}
     */
    async deleteYoutubeConnection() {
      await socialMediaAgent.deleteYoutubeConnection();
    },

    /**
     * @param {string} connectionId
     * @param {FacebookPage} connectionId
     * @returns {Promise<void>}
     */
    async updateFacebookConnectionPage(connectionId, data) {
      await socialMediaAgent.updateFacebookConnectionPage(connectionId, data);
    },

    /**
     * @param {string} connectionId
     * @returns {Promise<void>}
     */
    async syncFacebookPageDetails(connectionId) {
      await socialMediaAgent.syncFacebookPageDetails(connectionId);
    },

    /**
     * @returns {Promise<CustomRtmpSettings[]>}
     */
    async getConnectedCustomRtmpPlatforms() {
      const { socialMediaPlatforms = [] } = await socialMediaAgent.getRTMPConnections();

      return (socialMediaPlatforms).map(platform => {
        const { customDisplayName, displayName } = platform;
        platform.connectionName = customDisplayName ? ` ${displayName} - ${customDisplayName}` : ` ${displayName}`;
        return platform;
      });
    },

    /**
     * @param {CustomRtmpSettings} data
     * @returns {Promise<string>}
     */
    async createCustomRtmpIntegration(data) {
      return socialMediaAgent.createRTMPConnection(data);
    },

    /**
     * @param {string} rtmpId
     * @param {CustomRtmpSettings} data
     * @returns {Promise<void>}
     */
    async updateCustomRtmpSettings(rtmpId, data) {
      await socialMediaAgent.updateRTMPConnection(rtmpId, data);
    },

    /**
     * @param {string} rtmpId
     * @returns {Promise<void>}
     */
    async removeCustomRtmpIntegration(rtmpId) {
      await socialMediaAgent.deleteRTMPConnection(rtmpId);
    },
  };
};

/** @typedef {ReturnType<(typeof genService)>} SocialMediaService */

/**
 * Hooks a social media service instance to the current context
 * @param {string} orgId The organization id for the social media service
 * @returns {SocialMediaService}
 */
const useSocialMediaService = (orgId) => {
  const idToken = useIdToken();

  const [service, setService] = useState(() => genService(orgId, idToken));
  useEffect(() => void setService(genService(orgId, idToken)), [idToken, orgId]);

  return service;
};

export default useSocialMediaService;
